
// Comprehensive emoji validation and handling utilities

/**
 * Enhanced emoji validation that checks for Unicode emoji characters
 */
export const isValidEmoji = (str: string): boolean => {
  if (!str || typeof str !== 'string') {
    console.warn('Invalid emoji input:', str);
    return false;
  }

  // Trim whitespace
  const trimmed = str.trim();
  if (!trimmed) return false;

  // Check for UUID pattern (common source of corruption)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(trimmed)) {
    console.error('UUID detected as emoji:', trimmed);
    return false;
  }

  // Check for text-based emoji codes like :smile:, :heart:, etc.
  const textEmojiPattern = /^:[a-zA-Z0-9_+-]+:$/;
  if (textEmojiPattern.test(trimmed)) {
    console.error('Text-based emoji code detected:', trimmed);
    return false;
  }

  // Check for escaped sequences or HTML entities
  if (trimmed.includes('&') && trimmed.includes(';')) {
    console.error('HTML entity detected as emoji:', trimmed);
    return false;
  }

  // Enhanced Unicode emoji validation
  // This regex matches various emoji patterns including:
  // - Basic emojis with presentation selector
  // - Emoji with skin tone modifiers
  // - Multi-character emojis (like flags, families)
  // - Zero-width joiner sequences
  const emojiRegex = /^(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])+$/;
  
  try {
    // Additional check: try to measure the visual length
    // Valid emojis should have a visual length that's reasonable
    if (trimmed.length > 20) {
      console.error('Emoji too long:', trimmed);
      return false;
    }

    return emojiRegex.test(trimmed);
  } catch (error) {
    console.error('Error validating emoji:', error);
    return false;
  }
};

/**
 * Sanitizes emoji for database storage with enhanced normalization
 */
export const sanitizeEmojiForStorage = (emoji: string): string => {
  console.log('Sanitizing emoji for storage:', emoji);
  
  if (!isValidEmoji(emoji)) {
    console.error('Invalid emoji rejected for storage:', emoji);
    throw new Error(`Invalid emoji: ${emoji}`);
  }
  
  // Normalize Unicode to canonical form and trim whitespace
  const sanitized = emoji.normalize('NFC').trim();
  console.log('Sanitized emoji for storage:', sanitized);
  return sanitized;
};

/**
 * Sanitizes emoji from database retrieval with fallback handling
 */
export const sanitizeEmojiFromStorage = (emoji: string): string => {
  console.log('Sanitizing emoji from storage:', emoji);
  
  if (!emoji || typeof emoji !== 'string') {
    console.error('Invalid emoji data from storage:', emoji);
    return '❓'; // Fallback emoji
  }

  const trimmed = emoji.trim();
  
  if (!isValidEmoji(trimmed)) {
    console.error('Invalid emoji detected from storage, using fallback:', trimmed);
    return '❓'; // Fallback emoji
  }
  
  const sanitized = trimmed.normalize('NFC');
  console.log('Sanitized emoji from storage:', sanitized);
  return sanitized;
};

/**
 * Creates a fallback display for invalid emojis
 */
export const getEmojiDisplayFallback = (originalEmoji: string): string => {
  console.warn('Using fallback for invalid emoji:', originalEmoji);
  return '❓';
};

/**
 * Validates and processes emoji with comprehensive error handling
 */
export const processEmoji = (emoji: string, context: string = 'unknown'): string => {
  console.log(`Processing emoji in context: ${context}`, emoji);
  
  try {
    if (!emoji || typeof emoji !== 'string') {
      console.error(`Invalid emoji type in ${context}:`, emoji);
      return getEmojiDisplayFallback(emoji);
    }

    const trimmed = emoji.trim();
    
    if (!isValidEmoji(trimmed)) {
      console.error(`Invalid emoji in ${context}:`, trimmed);
      return getEmojiDisplayFallback(trimmed);
    }
    
    return sanitizeEmojiFromStorage(trimmed);
  } catch (error) {
    console.error(`Error processing emoji in ${context}:`, error);
    return getEmojiDisplayFallback(emoji);
  }
};

/**
 * Converts text-based emoji codes to Unicode emojis
 */
export const convertTextEmojiToUnicode = (text: string): string => {
  const emojiMap: Record<string, string> = {
    ':smile:': '😊',
    ':heart:': '❤️',
    ':thumbsup:': '👍',
    ':thumbsdown:': '👎',
    ':fire:': '🔥',
    ':star:': '⭐',
    ':laugh:': '😂',
    ':cry:': '😢',
    ':angry:': '😠',
    ':love:': '😍'
  };

  return emojiMap[text] || text;
};
